import React from "react";
import {Search, X} from "lucide-react";
import Input from "../ui/Input";
import {cn} from "../../utils/cn";

/**
 * Combined filter bar — search input + slot for additional filters + active chips below.
 *
 *   <FilterBar
 *     search={query}
 *     onSearchChange={…}
 *     searchPlaceholder="Search users…"
 *     chips={[{label: "Role: Manager", onRemove: …}]}
 *     onClearAll={…}
 *   >
 *     <Select ...>...</Select>   // additional filter controls as children
 *   </FilterBar>
 */
export default function FilterBar({
  search,
  onSearchChange,
  searchPlaceholder = "Search…",
  chips = [],
  onClearAll,
  children,
  className,
}) {
  const hasChips = chips.length > 0;
  return (
    <div className={cn("flex flex-col gap-3", className)}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="w-full sm:w-72">
          <Input
            type="search"
            value={search ?? ""}
            onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            leadingIcon={Search}
          />
        </div>
        {children}
      </div>
      {hasChips && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-ink-500">Filters:</span>
          {chips.map((c, i) => (
            <FilterChip key={i} onRemove={c.onRemove}>
              {c.label}
            </FilterChip>
          ))}
          {onClearAll && (
            <button
              type="button"
              onClick={onClearAll}
              className="text-xs font-medium text-brand-700 hover:underline ml-1"
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function FilterChip({children, onRemove}) {
  return (
    <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-0.5 rounded-full bg-brand-50 text-brand-700 text-xs font-medium border border-brand-100">
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove filter"
          className="inline-flex items-center justify-center h-4 w-4 rounded-full text-brand-500 hover:bg-brand-100 hover:text-brand-700"
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  );
}
